import {Heading, Text} from '@albert-io/atomic';
import React from 'react';

import MarkdownRenderer from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {criteriaFromRubric, toPlainRubric, totalPoints} from 'lib/rubric/criteria';

interface RubricPreviewProps {
  /**
   * The question's rubric, as returned by `question.getRubric()`: an Immutable Map
   * in app code (has `.toJS()`), a plain object in tests, or null/undefined when the
   * FRQ has no rubric. Kept as `unknown` so this component stays decoupled from the
   * two different question models (authoring vs. QuestionModelV3) that mount it.
   */
  rubric: unknown;
  /**
   * Rendered in place of the rubric when the FRQ has no criteria rubric. Lets a caller swap the
   * rubric in for another node (e.g. the question's normal Explanation) so it's never silently
   * dropped.
   */
  fallback?: React.ReactNode;
}

/**
 * Whether `RubricPreview` would render the given rubric — i.e. whether it is a criteria rubric.
 * Exported so mounts can coordinate surrounding content (e.g. hide the standard Explanation) with
 * the exact same rule the component applies internally.
 */
export function useRubricPreviewVisible(rubric: unknown): boolean {
  return criteriaFromRubric(toPlainRubric(rubric)).length > 0;
}

/**
 * Read-only render of a free-response question's structured "criteria" rubric, shown
 * in the question preview (Dennis editor) and the post-submission footer. Renders the
 * rubric when the question has a criteria one; otherwise it renders `fallback` (or
 * nothing), so a caller can swap the rubric in for the question's normal Explanation.
 * Editing stays in the authoring `RubricEditor`; the layout
 * mirrors that editor (total-points pill + a card per criterion) so authors see the
 * same rubric shape they edit.
 */
const RubricPreview = ({rubric, fallback = null}: RubricPreviewProps) => {
  const visible = useRubricPreviewVisible(rubric);
  const criteria = criteriaFromRubric(toPlainRubric(rubric));

  if (!visible) {
    return <>{fallback}</>;
  }

  return (
    <section className='mt-x4 mb-x4' aria-label='Rubric'>
      <div className='gap-x2 flex items-baseline justify-between'>
        <Heading as='h2' size='s' uppercase className='my-x0'>
          Rubric
        </Heading>
        <div className='gap-x1 bg-background-brand-subtle px-x2 py-x0-half inline-flex shrink-0 items-center rounded-full whitespace-nowrap'>
          <Text as='span' size='s' color='secondary'>
            Total possible points
          </Text>
          <Text as='span' bold>
            {totalPoints(criteria)}
          </Text>
        </div>
      </div>

      <ol className='m-x0 mt-x2 gap-x2 p-x0 flex list-none flex-col'>
        {criteria.map((criterion) => (
          <li key={criterion.id}>
            <div className='gap-x2 rounded-x1 border-border-primary background-primary p-x2 flex items-start justify-between border border-solid'>
              <div className='gap-x0-half flex flex-col'>
                <Text bold>{criterion.title || 'Untitled criterion'}</Text>
                {criterion.description ? (
                  <MarkdownRenderer
                    text={criterion.description}
                    className='text-s text-content-secondary'
                  />
                ) : null}
              </div>
              <Text as='span' color='secondary' className='whitespace-nowrap'>
                {`${criterion.maxScore} ${criterion.maxScore === 1 ? 'pt' : 'pts'}`}
              </Text>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
};

export default RubricPreview;
